import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaArrowLeft, FaEdit, FaTrash } from "react-icons/fa";
import "../assets/css/ViewCustomer.css";
import "../assets/css/EditCustomer.css";
import "../assets/css/Projects.css";
import "../assets/css/DigixLeads.css";
import axiosInstance from "../components/api/axiosInstance";
import UserStoriesTable from "./UserStoriesTable";
import ProjectFileAttachments from "./ProjectFileAttachments";
import TablePagination, { DEFAULT_ROWS_PER_PAGE_OPTIONS } from "../components/TablePagination";
// AddProjectPayment is rendered in a dedicated page now.
import { canPerformActionOnPage, PAGES, ACTIONS } from "../utils/roleAccess";
import { useResizableColumns } from "./issues/useResizableColumns";
import { SupportSortableHeader } from "./SupportSortableHeader";

const SUBSCRIPTION_TABLE_DEFAULT_WIDTHS = [160, 120, 120, 110, 100, 120, 140, 140, 96];
const PROJECT_SUBPROJECTS_ROWS_PER_PAGE_KEY = "projectSubprojectsRowsPerPage";

/** Default column widths for Project Payments (Date … Remarks; Actions uses last slot when shown). */
const PAYMENTS_TABLE_DEFAULT_WIDTHS = [112, 120, 96, 150, 110, 130, 160, 110, 200, 128];

function getPaymentRowId(pmt) {
  const id = pmt.Payment_Id ?? pmt.payment_id ?? pmt.id;
  return id != null ? String(id) : "";
}

function getPaymentSortValue(pmt, columnId) {
  switch (columnId) {
    case "date": {
      const d = pmt.Payment_Date ?? pmt.payment_date;
      if (d == null || d === "") return 0;
      const t = new Date(d).getTime();
      return Number.isFinite(t) ? t : 0;
    }
    case "amount":
      return Number(pmt.Amount ?? pmt.amount ?? 0);
    case "currency": {
      const code = String(pmt.Currency_Code ?? pmt.currency_code ?? "").trim().toUpperCase();
      const sym = String(pmt.Currency_Symbol ?? pmt.currency_symbol ?? "").trim();
      const label = sym || code || String(pmt.Currency_Name ?? pmt.currency_name ?? "");
      return label.toLowerCase();
    }
    case "paymentMode":
      return String(pmt.Payment_Mode_Name ?? pmt.payment_mode_name ?? "").toLowerCase();
    case "type":
      return String(pmt.Payment_Type ?? pmt.payment_type ?? "").toLowerCase();
    case "totalAmount":
      return Number(pmt.Total_Amount ?? pmt.total_amount ?? 0);
    case "transactionRef":
      return String(pmt.Transaction_Ref ?? pmt.transaction_ref ?? "").toLowerCase();
    case "status":
      return String(pmt.Payment_Status ?? pmt.payment_status ?? "").toLowerCase();
    case "remarks":
      return String(pmt.Remarks ?? pmt.remarks ?? "").toLowerCase();
    default:
      return "";
  }
}

function getSubscriptionRowId(s) {
  const id = s.Subscription_Id ?? s.subscription_id ?? s.id;
  return id != null ? String(id) : "";
}

function getSubscriptionSortValue(s, columnId) {
  switch (columnId) {
    case "plan":
      return String(s.Plan_Name ?? s.plan_name ?? "").toLowerCase();
    case "startDate": {
      const t = new Date(s.Start_Date ?? s.start_date ?? "").getTime();
      return Number.isFinite(t) ? t : 0;
    }
    case "endDate": {
      const t = new Date(s.End_Date ?? s.end_date ?? "").getTime();
      return Number.isFinite(t) ? t : 0;
    }
    case "duration":
      return Number(s.Duration_Days ?? s.duration_days ?? 0);
    case "status":
      return String(s.Status ?? s.status ?? "").toLowerCase();
    case "amount":
      return Number(s.Amount ?? s.amount ?? 0);
    case "createdOn": {
      const t = new Date(s.Created_On ?? s.created_on ?? "").getTime();
      return Number.isFinite(t) ? t : 0;
    }
    case "createdBy":
      return String(s.Created_By_Name ?? s.created_by_name ?? "").toLowerCase();
    default:
      return "";
  }
}

function getSubprojectRowId(sp) {
  const id = sp.Sub_Project_Id ?? sp.sub_project_id ?? sp.id;
  return id != null ? String(id) : "";
}

function getSubprojectSortValue(sp, columnId) {
  switch (columnId) {
    case "name":
      return String(sp.Sub_Project_Name ?? sp.sub_project_name ?? "").toLowerCase();
    case "shortCode":
      return String(sp.Short_Code ?? sp.short_code ?? "").toLowerCase();
    case "domain":
      return String(sp.Domain ?? sp.domain ?? "").toLowerCase();
    case "category":
      return String(sp.Category ?? sp.category ?? "").toLowerCase();
    case "status":
      return String(sp.Status ?? sp.status ?? "").toLowerCase();
    case "startDate": {
      const t = new Date(sp.Start_Date ?? sp.start_date ?? "").getTime();
      return Number.isFinite(t) ? t : 0;
    }
    case "endDate": {
      const t = new Date(sp.End_Date ?? sp.end_date ?? "").getTime();
      return Number.isFinite(t) ? t : 0;
    }
    case "visibility":
      return String(sp.Visibility ?? sp.visibility ?? "").toLowerCase();
    default:
      return "";
  }
}

function ViewProject({ projectId, onBack, onEditProject, onPageTitleReady }) {
  const navigate = useNavigate();
  const location = useLocation();
  const role = (() => {
    try {
      const u = localStorage.getItem("user");
      if (!u) return null;
      const user = JSON.parse(u);
      const primaryRole = Array.isArray(user.roles) && user.roles.length > 0 ? user.roles[0] : null;
      return (
        (primaryRole && (primaryRole.role_name || primaryRole.name || primaryRole.role)) ||
        user.role_name ||
        user.role ||
        null
      );
    } catch {
      return null;
    }
  })();
  const canEditProject = canPerformActionOnPage(PAGES.PROJECTS, ACTIONS.EDIT, role);
  const canAddSubproject = canPerformActionOnPage(PAGES.PROJECTS, ACTIONS.ADD, role);
  const canEditSubproject = canPerformActionOnPage(PAGES.PROJECTS, ACTIONS.EDIT, role);
  const canDeleteSubproject = canPerformActionOnPage(PAGES.PROJECTS, ACTIONS.DELETE, role);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [subprojects, setSubprojects] = useState([]);
  const [loadingSubprojects, setLoadingSubprojects] = useState(false);
  const [subprojectPage, setSubprojectPage] = useState(1);
  const [subprojectRowsPerPage, setSubprojectRowsPerPage] = useState(() => {
    try {
      const saved = localStorage.getItem(PROJECT_SUBPROJECTS_ROWS_PER_PAGE_KEY);
      const parsed = parseInt(saved, 10);
      if (DEFAULT_ROWS_PER_PAGE_OPTIONS.includes(parsed)) {
        return parsed;
      }
    } catch {
      /* ignore */
    }
    return 5;
  });
  const [domains, setDomains] = useState([]);
  const [loadingDomains, setLoadingDomains] = useState(true);
  const [subprojectColumnWidths, setSubprojectColumnWidths] = useState({});
  const subprojectResizeRef = React.useRef({ startX: 0, startWidth: 0, columnIndex: null });
  const [subprojectNameFilter, setSubprojectNameFilter] = useState("");
  const [subprojectDomainFilter, setSubprojectDomainFilter] = useState("");
  const [subprojectCategoryFilter, setSubprojectCategoryFilter] = useState("");
  const [subprojectStatusFilter, setSubprojectStatusFilter] = useState("");
  const [subprojectSortColumn, setSubprojectSortColumn] = useState(null);
  const [subprojectSortDirection, setSubprojectSortDirection] = useState("asc");
  const [usersRoleMap, setUsersRoleMap] = useState({});
  const [payments, setPayments] = useState([]);
  const [paymentSortColumn, setPaymentSortColumn] = useState(null);
  const [paymentSortDirection, setPaymentSortDirection] = useState("asc");
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [subscriptions, setSubscriptions] = useState([]);
  const [subscriptionSortColumn, setSubscriptionSortColumn] = useState(null);
  const [subscriptionSortDirection, setSubscriptionSortDirection] = useState("asc");
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(false);
  const [subscriptionsError, setSubscriptionsError] = useState("");
  // Add Payment is handled via a dedicated page now.

  const { getColProps: getSubscriptionColProps, ResizeHandle: SubscriptionResizeHandle } =
    useResizableColumns(SUBSCRIPTION_TABLE_DEFAULT_WIDTHS);
  const { getColProps: getPaymentColProps, ResizeHandle: PaymentResizeHandle } = useResizableColumns(
    PAYMENTS_TABLE_DEFAULT_WIDTHS
  );

  const sortedPayments = useMemo(() => {
    if (!payments.length) return [];
    const col = paymentSortColumn;
    const dir = paymentSortDirection;
    const list = [...payments];
    if (!col) return list;
    list.sort((a, b) => {
      const va = getPaymentSortValue(a, col);
      const vb = getPaymentSortValue(b, col);
      let cmp = 0;
      if (typeof va === "number" && typeof vb === "number") {
        cmp = va - vb;
      } else {
        cmp = String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: "base" });
      }
      if (cmp !== 0) return dir === "asc" ? cmp : -cmp;
      return getPaymentRowId(a).localeCompare(getPaymentRowId(b));
    });
    return list;
  }, [payments, paymentSortColumn, paymentSortDirection]);

  const handlePaymentSort = (columnId) => {
    if (paymentSortColumn === columnId) {
      setPaymentSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setPaymentSortColumn(columnId);
      setPaymentSortDirection("asc");
    }
  };

  const sortedSubscriptions = useMemo(() => {
    if (!subscriptions.length) return [];
    if (!subscriptionSortColumn) return [...subscriptions];
    const dir = subscriptionSortDirection;
    const col = subscriptionSortColumn;
    return [...subscriptions].sort((a, b) => {
      const va = getSubscriptionSortValue(a, col);
      const vb = getSubscriptionSortValue(b, col);
      let cmp = 0;
      if (typeof va === "number" && typeof vb === "number") {
        cmp = va - vb;
      } else {
        cmp = String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: "base" });
      }
      if (cmp !== 0) return dir === "asc" ? cmp : -cmp;
      return getSubscriptionRowId(a).localeCompare(getSubscriptionRowId(b));
    });
  }, [subscriptions, subscriptionSortColumn, subscriptionSortDirection]);

  const handleSubscriptionSort = (columnId) => {
    if (subscriptionSortColumn === columnId) {
      setSubscriptionSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSubscriptionSortColumn(columnId);
      setSubscriptionSortDirection("asc");
    }
  };

  useEffect(() => {
    const fetchProject = async () => {
      if (!projectId) return;
      setLoading(true);
      setError("");
      try {
        let p = null;
        try {
          const response = await axiosInstance.get(`/project/${projectId}`);
          p = response.data?.data ?? response.data;
        } catch {
          const response = await axiosInstance.get(`/projects/${projectId}`);
          p = response.data?.data ?? response.data;
        }
        if (p) {
          setProject(p);
          if (onPageTitleReady) onPageTitleReady(p.Project_Name || p.project_name || "");
        }
      } catch (err) {
        console.error("Error fetching project:", err);
        setError(err.response?.data?.message || "Failed to load project details.");
      } finally {
        setLoading(false);
      }
    };
    fetchProject();
  }, [projectId]);

  useEffect(() => {
    const fetchUsersForRoles = async () => {
      try {
        const response = await axiosInstance.get("/users");
        const arr = Array.isArray(response.data)
          ? response.data
          : response.data?.data ?? response.data?.users ?? [];
        const map = {};
        (arr || []).forEach((u) => {
          const id = u.user_id ?? u.id ?? u.User_Id;
          if (id == null) return;
          const roleDisplay =
            (u.role ?? u.role_name ?? u.Role ?? u.Role_Name ?? "").toString().trim() ||
            (Array.isArray(u.roles) && u.roles.length > 0
              ? u.roles.map((r) => r.role_name ?? r.roleName ?? r.name ?? r.role ?? "").filter(Boolean).join(", ")
              : "");
          map[String(id)] = (roleDisplay || "").trim() || "—";
        });
        setUsersRoleMap(map);
      } catch (err) {
        console.error("Error fetching users for roles:", err);
        setUsersRoleMap({});
      }
    };
    fetchUsersForRoles();
  }, [projectId]);

  const fetchSubprojects = async () => {
    if (!projectId) return;
    setLoadingSubprojects(true);
    try {
      // Primary: same as Edit page – /sub-projects/project/:id
      // Primary: same as Edit page – /sub-projects/project/:id
      // Primary: same as Edit page – /sub-projects/project/:id
      // Primary: same as Edit page – /sub-projects/project/:id
      let response = await axiosInstance.get(`/sub-projects/project/${projectId}`);
      let raw = response.data;
      let arr = [];

      if (Array.isArray(raw)) {
        arr = raw;
      } else if (raw && Array.isArray(raw.sub_projects)) {
        arr = raw.sub_projects;
      } else if (raw && Array.isArray(raw.data)) {
        arr = raw.data;
      }

      // Fallback: legacy endpoints if primary is unavailable or empty
      // Fallback: legacy endpoints if primary is unavailable or empty
      // Fallback: legacy endpoints if primary is unavailable or empty
      // Fallback: legacy endpoints if primary is unavailable or empty
      // Fallback: legacy endpoints if primary is unavailable or empty
      // Fallback: legacy endpoints if primary is unavailable or empty
      // Fallback: legacy endpoints if primary is unavailable or empty
      if (!arr || arr.length === 0) {
        try {
          response = await axiosInstance.get(`/projects/${projectId}/sub-projects`);
          raw = response.data;
          arr = Array.isArray(raw) ? raw : raw?.sub_projects ?? raw?.data ?? [];
        } catch (legacyErr) {
          console.error("Legacy /projects/:id/sub-projects failed:", legacyErr);
          try {
            response = await axiosInstance.get("/sub-projects", { params: { project_id: projectId } });
            raw = response.data;
            arr = Array.isArray(raw) ? raw : raw?.sub_projects ?? raw?.data ?? [];
          } catch (fallbackErr) {
            console.error("Fallback /sub-projects?project_id failed:", fallbackErr);
          }
        }
      }

      setSubprojects(arr || []);
    } catch (err) {
      console.error("Error fetching subprojects:", err);
      try {
        const response = await axiosInstance.get("/sub-projects", { params: { project_id: projectId } });
        const raw = response.data;
        const arr = Array.isArray(raw) ? raw : raw?.sub_projects ?? raw?.data ?? [];
        setSubprojects(arr || []);
      } catch (fallbackErr) {
        console.error("Fallback fetch subprojects error:", fallbackErr);
        setSubprojects([]);
      }
    } finally {
      setLoadingSubprojects(false);
    }
  };

  useEffect(() => {
    if (project) fetchSubprojects();
  }, [projectId, project]);

  useEffect(() => {
    const fetchDomains = async () => {
      setLoadingDomains(true);
      try {
        const response = await axiosInstance.get("/domains");
        const arr = Array.isArray(response.data) ? response.data : response.data?.data || [];
        const mapped = arr
          .map((d) => ({
            id: d.Domain_Id ?? d.domain_id ?? d.id,
            name: d.Domain_Name ?? d.domain_name ?? d.name ?? "—"
          }))
          .filter((x) => x.id && x.name !== "—")
          .sort((a, b) => String(a.name).localeCompare(String(b.name)));
        setDomains(mapped);
      } catch (err) {
        console.error("Error fetching domains:", err);
        setDomains([]);
      } finally {
        setLoadingDomains(false);
      }
    };
    fetchDomains();
  }, []);

  const fetchPayments = async () => {
    if (!projectId) return;
    setLoadingPayments(true);
    try {
      let arr = [];
      const res1 = await axiosInstance.get("/project-payments", { params: { project_id: projectId } }).catch(() => null);
      if (res1?.data) {
        const raw = res1.data;
        arr = Array.isArray(raw) ? raw : raw?.payments ?? raw?.data ?? [];
      }
      if (arr.length === 0) {
        const res2 = await axiosInstance.get(`/projects/${projectId}/payments`).catch(() => null);
        if (res2?.data) {
          const raw = res2.data;
          arr = Array.isArray(raw) ? raw : raw?.payments ?? raw?.data ?? [];
        }
      }

      // Backend may return payments for all projects; filter by selected project.
      const projectIdStr = String(projectId);
      const filtered = (arr || []).filter((p) => {
        const pid = p.Project_Id ?? p.project_id ?? p.ProjectId ?? p.projectId ?? null;
        return pid == null ? true : String(pid) === projectIdStr;
      });

      // Normalize to a stable field set for the table.
      const normalized = filtered.map((p) => ({
        Payment_Id: p.Payment_Id ?? p.payment_id ?? p.id,
        Payment_Date: p.Payment_Date ?? p.payment_date ?? p.PaymentDate ?? p.paymentDate,
        Amount: p.Amount ?? p.amount,
        Currency_Code: p.Currency_Code ?? p.currency_code ?? p.CurrencyName ?? p.currency_name,
        Currency_Name: p.Currency_Name ?? p.currency_name,
        Currency_Symbol: p.Currency_Symbol ?? p.currency_symbol,
        Payment_Mode_Id: p.Payment_Mode_Id ?? p.payment_mode_id,
        Payment_Mode_Name: p.Payment_Mode_Name ?? p.payment_mode_name,
        Payment_Status: p.Payment_Status ?? p.payment_status,
        Payment_Type: p.Payment_Type ?? p.payment_type,
        Project_Id: p.Project_Id ?? p.project_id,
        Project_Name: p.Project_Name ?? p.project_name,
        Remarks: p.Remarks ?? p.remarks,
        Total_Amount: p.Total_Amount ?? p.total_amount,
        Transaction_Ref: p.Transaction_Ref ?? p.transaction_ref,
        Created_By: p.Created_By ?? p.created_by,
        Created_On: p.Created_On ?? p.created_on,
        Updated_By: p.Updated_By ?? p.updated_by,
        Updated_On: p.Updated_On ?? p.updated_on
      }));

      setPayments(normalized);
    } catch (err) {
      setPayments([]);
    } finally {
      setLoadingPayments(false);
    }
  };

  useEffect(() => {
    if (projectId) fetchPayments();
  }, [projectId, location.search]);

  const fetchSubscriptions = async () => {
    if (!projectId) return;
    setLoadingSubscriptions(true);
    setSubscriptionsError("");
    try {
      const res = await axiosInstance.get(`/fico/project-subscriptions/${projectId}`);
      const raw = res?.data;
      const arr = Array.isArray(raw) ? raw : raw?.data ?? raw?.subscriptions ?? [];
      const mapped = (arr || []).map((s) => ({
        Subscription_Id: s.Subscription_Id ?? s.subscription_id ?? s.id,
        Amount: s.Amount ?? s.amount,
        Duration_Days: s.Duration_Days ?? s.duration_days,
        Start_Date: s.Start_Date ?? s.start_date,
        End_Date: s.End_Date ?? s.end_date,
        Status: s.Status ?? s.status,
        Plan_Id: s.Plan_Id ?? s.plan_id,
        Plan_Name: s.Plan_Name ?? s.plan_name,
        Project_Id: s.Project_Id ?? s.project_id,
        Project_Name: s.Project_Name ?? s.project_name,
        Created_By: s.Created_By ?? s.created_by,
        Created_By_Name: s.Created_By_Name ?? s.created_by_name,
        Created_On: s.Created_On ?? s.created_on,
        Updated_By: s.Updated_By ?? s.updated_by,
        Updated_By_Name: s.Updated_By_Name ?? s.updated_by_name,
        Updated_On: s.Updated_On ?? s.updated_on
      }));
      setSubscriptions(mapped);
    } catch (err) {
      console.error("Error fetching project subscriptions:", err);
      setSubscriptions([]);
      setSubscriptionsError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          "Failed to load subscriptions."
      );
    } finally {
      setLoadingSubscriptions(false);
    }
  };

  useEffect(() => {
    if (projectId) fetchSubscriptions();
  }, [projectId, location.search]);

  const filteredSubprojects = useMemo(() => {
    const name = subprojectNameFilter.trim().toLowerCase();
    return (subprojects || []).filter((sp) => {
      const spName = (sp.Sub_Project_Name || sp.sub_project_name || "").toLowerCase();
      const spDomain = sp.Domain || sp.domain || "";
      const spCategory = sp.Category || sp.category || "";
      const spStatus = sp.Status || sp.status || "";

      if (name && !spName.includes(name)) return false;
      if (subprojectDomainFilter && spDomain !== subprojectDomainFilter) return false;
      if (subprojectCategoryFilter && spCategory !== subprojectCategoryFilter) return false;
      if (subprojectStatusFilter && spStatus !== subprojectStatusFilter) return false;
      return true;
    });
  }, [subprojects, subprojectNameFilter, subprojectDomainFilter, subprojectCategoryFilter, subprojectStatusFilter]);

  const sortedSubprojects = useMemo(() => {
    if (!filteredSubprojects.length) return [];
    if (!subprojectSortColumn) return [...filteredSubprojects];
    const dir = subprojectSortDirection;
    const col = subprojectSortColumn;
    return [...filteredSubprojects].sort((a, b) => {
      const va = getSubprojectSortValue(a, col);
      const vb = getSubprojectSortValue(b, col);
      let cmp = 0;
      if (typeof va === "number" && typeof vb === "number") {
        cmp = va - vb;
      } else {
        cmp = String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: "base" });
      }
      if (cmp !== 0) return dir === "asc" ? cmp : -cmp;
      return getSubprojectRowId(a).localeCompare(getSubprojectRowId(b));
    });
  }, [filteredSubprojects, subprojectSortColumn, subprojectSortDirection]);

  const handleSubprojectSort = (columnId) => {
    setSubprojectPage(1);
    if (subprojectSortColumn === columnId) {
      setSubprojectSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSubprojectSortColumn(columnId);
      setSubprojectSortDirection("asc");
    }
  };

  const subprojectCategoryOptions = useMemo(() => {
    const set = new Set();
    (subprojects || []).forEach((sp) => {
      const v = sp.Category || sp.category;
      if (v && v !== "—") set.add(v);
    });
    return Array.from(set).sort();
  }, [subprojects]);

  const subprojectStatusOptions = useMemo(() => {
    const set = new Set();
    (subprojects || []).forEach((sp) => {
      const v = sp.Status || sp.status;
      if (v && v !== "—") set.add(v);
    });
    return Array.from(set).sort();
  }, [subprojects]);

  const totalSubprojectPages = Math.max(1, Math.ceil(sortedSubprojects.length / subprojectRowsPerPage));
  const subprojectStartIndex = (subprojectPage - 1) * subprojectRowsPerPage;
  const subprojectEndIndex = subprojectStartIndex + subprojectRowsPerPage;
  const currentSubprojects = sortedSubprojects.slice(subprojectStartIndex, subprojectEndIndex);

  useEffect(() => {
    if (subprojectPage > totalSubprojectPages && totalSubprojectPages > 0) {
      setSubprojectPage(1);
    }
  }, [totalSubprojectPages, subprojectPage]);

  const handleSubprojectRowsPerPageChange = (next) => {
    setSubprojectRowsPerPage(next);
    setSubprojectPage(1);
    localStorage.setItem(PROJECT_SUBPROJECTS_ROWS_PER_PAGE_KEY, String(next));
  };

  const handleDeleteSubproject = async (subprojectId) => {
    if (!subprojectId) return;
    const confirmed = window.confirm("Are you sure you want to delete this subproject? This will deactivate it.");
    if (!confirmed) return;
    try {
      const response = await axiosInstance.delete(`/sub-project/${subprojectId}`);
      const msg = response?.data?.message || "Subproject deactivated successfully";
      alert(msg);
      setSubprojects((prev) =>
        (prev || []).filter(
          (sp) => (sp.Sub_Project_Id || sp.sub_project_id) !== subprojectId
        )
      );
    } catch (err) {
      console.error("Error deleting subproject:", err);
      alert(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Failed to delete subproject."
      );
    }
  };

  const InfoItem = ({ label, value, className = "", hideLabel = false }) => {
    const hasValue = value && value !== "—" && value !== "";
    return (
      <div className={["viewlead-info-item", className].filter(Boolean).join(" ")}>
        {!hideLabel && (
          <span className={`viewlead-info-label ${hasValue ? "has-value" : ""}`}>{label}: </span>
        )}
        <span className={`viewlead-info-value ${!hasValue ? "muted" : ""}`}>{hasValue ? value : "—"}</span>
      </div>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
      const d = new Date(dateStr);
      return isNaN(d.getTime()) ? "—" : d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
    } catch {
      return "—";
    }
  };

  const formatProjectAmount = (proj) => {
    const raw = proj.Total_Cost ?? proj.total_cost;
    if (raw == null || raw === "") return null;
    const n = Number(String(raw).replace(/,/g, ""));
    if (Number.isNaN(n)) return String(raw);
    const sym = proj.Currency_Symbol ?? proj.currency_symbol ?? "";
    const code = String(proj.Currency_Code ?? proj.currency_code ?? "").trim().toUpperCase();
    const formatted = n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const symbolFromCode =
      code === "INR"
        ? "₹"
        : code === "USD"
          ? "$"
          : "";

    if (sym) return `${sym}${formatted}`;
    if (symbolFromCode) return `${symbolFromCode}${formatted}`;
    if (code) return `${code} ${formatted}`; // fallback if backend returns an unknown code
    return `₹${formatted}`;
  };

  const getSubprojectColumnWidth = (columnIndex) => {
    if (subprojectColumnWidths[columnIndex]) {
      return `${subprojectColumnWidths[columnIndex]}px`;
    }
    return undefined;
  };

  const getSubprojectColumnMinWidth = (columnIndex) => {
    if (subprojectColumnWidths[columnIndex]) {
      return `${subprojectColumnWidths[columnIndex]}px`;
    }
    return undefined;
  };

  const handleSubprojectResizeStart = (e, columnIndex) => {
    e.preventDefault();
    e.stopPropagation();

    const th = e.currentTarget.parentElement;
    if (!th) return;

    const startWidth = th.offsetWidth;
    const startX = e.clientX;

    subprojectResizeRef.current = {
      startX,
      startWidth,
      columnIndex
    };

    document.body.classList.add("resizing");

    const handleMouseMove = (moveEvent) => {
      if (subprojectResizeRef.current.columnIndex === null) return;
      const diff = moveEvent.clientX - subprojectResizeRef.current.startX;
      const newWidth = Math.max(80, subprojectResizeRef.current.startWidth + diff);

      setSubprojectColumnWidths((prev) => ({
        ...prev,
        [subprojectResizeRef.current.columnIndex]: newWidth
      }));
    };

    const handleMouseUp = () => {
      subprojectResizeRef.current = { startX: 0, startWidth: 0, columnIndex: null };
      document.body.classList.remove("resizing");
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  if (loading) {
    return (
      <div className="viewlead-container">
        <div className="viewlead-loading">Loading project details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="viewlead-container">
        <button className="viewlead-back-link" onClick={onBack}>
          <FaArrowLeft /> Back
        </button>
        <div className="error-message" style={{ backgroundColor: "#fee", color: "#c33", padding: "12px", borderRadius: "4px", marginBottom: "20px", fontSize: "14px" }}>
          {error}
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="viewlead-container">
        <button className="viewlead-back-link" onClick={onBack}>
          <FaArrowLeft /> Back
        </button>
        <div className="viewlead-loading">Project not found.</div>
      </div>
    );
  }

  const p = project;

  return (
    <div className="viewlead-container">
      <div className="viewlead-header">
        <button className="viewlead-back-link" onClick={onBack}>
          <FaArrowLeft /> Back
        </button>
        {canEditProject && (
          <button
            type="button"
            className="btn-subproject-primary"
            onClick={() => onEditProject && onEditProject(projectId)}
          >
            Edit Project
          </button>
        )}
      </div>

      <div
        style={{
          display: "flex",
          gap: 16,
          alignItems: "stretch",
          flexWrap: "nowrap",
          marginBottom: 20
        }}
      >
        <div
          className="viewlead-card"
          style={{ flex: "2 1 0", minWidth: 260 }}
        >
          <h3 className="viewlead-section-title">Project Information</h3>
          <div className="viewlead-info-grid">
            <InfoItem label="Project ID" value={String(p.Project_Id ?? p.project_id ?? "")} />
            <InfoItem label="Project Name" value={p.Project_Name || p.project_name} />
            <InfoItem label="Short Code" value={p.Short_Code || p.short_code} />
            <InfoItem label="Domain" value={p.Domain || p.domain} />
            <InfoItem label="Category" value={p.Category || p.category} />
            <InfoItem label="Status" value={p.Status || p.status} />
            <InfoItem label="Visibility" value={p.Visibility || p.visibility} />
            <InfoItem
              label="Priority Sequence"
              value={p.Priority_Seq || p.priority_seq || "—"}
            />
            <InfoItem label="Start Date" value={formatDate(p.Start_Date || p.start_date)} />
            <InfoItem label="End Date" value={formatDate(p.End_Date || p.end_date)} />
            <InfoItem
              label="Actual Start Date"
              value={formatDate(p.Actual_Start_Date || p.actual_start_date)}
            />
            <InfoItem
              label="Actual End Date"
              value={formatDate(p.Actual_End_Date || p.Actual_Date || p.actual_end_date || p.actual_date)}
            />
            <InfoItem label="Project amount" value={formatProjectAmount(p) || "—"} />
          </div>

          <h3 className="viewlead-section-title no-border">Description</h3>
          <div className="viewlead-info-grid" style={{ gridTemplateColumns: "1fr" }}>
            <InfoItem label="Description" value={p.Description || p.description} hideLabel />
          </div>
          <h3 className="viewlead-section-title no-border">Notes</h3>
          <div className="viewlead-info-grid" style={{ gridTemplateColumns: "1fr" }}>
            <InfoItem label="Notes" value={p.Notes || p.notes} hideLabel />
          </div>
        </div>

        <div
          className="viewlead-card"
          style={{ flex: "1 1 0", minWidth: 260, maxWidth: 480 }}
        >
          <h3 className="viewlead-section-title">Customer</h3>
          <div className="viewlead-info-grid">
            {(() => {
              const customerId = p.client_id ?? p.client_id;
              const customerName = p.Customer_Name || p.customer_name || "—";
              if (customerId && customerName && customerName !== "—") {
                return (
                  <div className="viewlead-info-item viewlead-info-inline-row">
                    <span className="viewlead-info-label has-value">Client Name: </span>
                    <span
                      className="viewlead-info-value viewlead-link"
                      role="button"
                      tabIndex={0}
                      onClick={() => navigate(`/clients/${customerId}/view`)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          navigate(`/clients/${customerId}/view`);
                        }
                      }}
                    >
                      {customerName}
                    </span>
                  </div>
                );
              }
              return <InfoItem className="viewlead-info-inline-row" label="Customer Name" value={customerName} />;
            })()}
          </div>

          <h3 className="viewlead-section-title">Team Members</h3>
          <div className="viewlead-info-grid">
            {p.Users && p.Users.length > 0 ? (
              <div style={{ gridColumn: "1 / -1" }}>
                {p.Users.map((u) => {
                  const userId = u.User_Id ?? u.user_id;
                  const roleDisplay = usersRoleMap[String(userId)] ?? u.Role_Name ?? u.role_name ?? u.Role ?? u.role ?? "—";
                  return (
                    <div key={u.User_Id || u.user_id} style={{ marginBottom: 8 }}>
                      <span style={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                        {u.User_Name || u.user_name || `${u.First_Name || u.first_name || ""} ${u.Last_Name || u.last_name || ""}`.trim() || "—"}
                      </span>
                      <span style={{ color: "#888", marginLeft: 8 }}>
                        ({roleDisplay})
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <InfoItem label="Team Members" value="No team members assigned" />
            )}
          </div>

          <h3 className="viewlead-section-title">Audit</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <InfoItem label="Created By" value={p.Created_By_Name || p.created_by_name} />
            <InfoItem label="Created On" value={formatDate(p.Created_On || p.created_on)} />
            <InfoItem label="Updated By" value={p.Updated_By_Name || p.updated_by_name} />
            <InfoItem label="Updated On" value={formatDate(p.Updated_On || p.updated_on)} />
          </div>
        </div>
      </div>

      <div className="viewlead-card issue-attachments-card project-attachments-card">
        <ProjectFileAttachments
          project={project}
          projectId={projectId}
          canUpload={canEditProject}
          title="Project attachments"
          onRefresh={async () => {
            if (!projectId) return;
            try {
              const response = await axiosInstance.get(`/projects/${projectId}`);
              const pr = response.data?.data || response.data;
              if (pr) setProject(pr);
            } catch (err) {
              console.error("Error refetching project:", err);
            }
          }}
        />
      </div>

      <div className="viewlead-card project-payments-card">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h3 className="viewlead-section-title" style={{ margin: 0 }}>Project Payments</h3>
          {canEditProject && (
            <button
              type="button"
              className="btn-subproject-primary"
              onClick={() => navigate(`/projects/${projectId}/payments/add`)}
            >
              Add Payment
            </button>
          )}
        </div>
        {loadingPayments ? (
          <div className="project-subprojects-loading" style={{ marginTop: 16 }}>Loading payments...</div>
        ) : payments.length > 0 ? (
          <div className="project-payments-table">
            <div className="table-wrapper">
              <div className="table-container">
                <table className="data-table projects-table-frozen project-payments-resizable-table">
                  <thead>
                    <tr>
                      <th className="projects-th-sortable" {...getPaymentColProps(0)}>
                        <div className="projects-th-sort-row projects-th-sort-row--center">
                          <SupportSortableHeader
                            columnId="date"
                            label="Date"
                            sortColumn={paymentSortColumn}
                            sortDirection={paymentSortDirection}
                            onSort={handlePaymentSort}
                          />
                        </div>
                        <PaymentResizeHandle index={0} />
                      </th>
                      <th className="projects-th-sortable" {...getPaymentColProps(1)}>
                        <div className="projects-th-sort-row projects-th-sort-row--center">
                          <SupportSortableHeader
                            columnId="amount"
                            label="Amount"
                            sortColumn={paymentSortColumn}
                            sortDirection={paymentSortDirection}
                            onSort={handlePaymentSort}
                          />
                        </div>
                        <PaymentResizeHandle index={1} />
                      </th>
                      <th className="projects-th-sortable" {...getPaymentColProps(2)}>
                        <div className="projects-th-sort-row projects-th-sort-row--center">
                          <SupportSortableHeader
                            columnId="currency"
                            label="Currency"
                            sortColumn={paymentSortColumn}
                            sortDirection={paymentSortDirection}
                            onSort={handlePaymentSort}
                          />
                        </div>
                        <PaymentResizeHandle index={2} />
                      </th>
                      <th className="projects-th-sortable" {...getPaymentColProps(3)}>
                        <div className="projects-th-sort-row projects-th-sort-row--center">
                          <SupportSortableHeader
                            columnId="paymentMode"
                            label="Payment Mode"
                            sortColumn={paymentSortColumn}
                            sortDirection={paymentSortDirection}
                            onSort={handlePaymentSort}
                          />
                        </div>
                        <PaymentResizeHandle index={3} />
                      </th>
                      <th className="projects-th-sortable" {...getPaymentColProps(4)}>
                        <div className="projects-th-sort-row projects-th-sort-row--center">
                          <SupportSortableHeader
                            columnId="type"
                            label="Type"
                            sortColumn={paymentSortColumn}
                            sortDirection={paymentSortDirection}
                            onSort={handlePaymentSort}
                          />
                        </div>
                        <PaymentResizeHandle index={4} />
                      </th>
                      <th className="projects-th-sortable" {...getPaymentColProps(5)}>
                        <div className="projects-th-sort-row projects-th-sort-row--center">
                          <SupportSortableHeader
                            columnId="totalAmount"
                            label="Total Amount"
                            sortColumn={paymentSortColumn}
                            sortDirection={paymentSortDirection}
                            onSort={handlePaymentSort}
                          />
                        </div>
                        <PaymentResizeHandle index={5} />
                      </th>
                      <th className="projects-th-sortable" {...getPaymentColProps(6)}>
                        <div className="projects-th-sort-row projects-th-sort-row--center">
                          <SupportSortableHeader
                            columnId="transactionRef"
                            label="Transaction Ref"
                            sortColumn={paymentSortColumn}
                            sortDirection={paymentSortDirection}
                            onSort={handlePaymentSort}
                          />
                        </div>
                        <PaymentResizeHandle index={6} />
                      </th>
                      <th className="projects-th-sortable" {...getPaymentColProps(7)}>
                        <div className="projects-th-sort-row projects-th-sort-row--center">
                          <SupportSortableHeader
                            columnId="status"
                            label="Status"
                            sortColumn={paymentSortColumn}
                            sortDirection={paymentSortDirection}
                            onSort={handlePaymentSort}
                          />
                        </div>
                        <PaymentResizeHandle index={7} />
                      </th>
                      <th className="projects-th-sortable" {...getPaymentColProps(8)}>
                        <div className="projects-th-sort-row projects-th-sort-row--center">
                          <SupportSortableHeader
                            columnId="remarks"
                            label="Remarks"
                            sortColumn={paymentSortColumn}
                            sortDirection={paymentSortDirection}
                            onSort={handlePaymentSort}
                          />
                        </div>
                        <PaymentResizeHandle index={8} />
                      </th>
                      {canEditProject && (
                        <th {...getPaymentColProps(9)}>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>Actions</div>
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedPayments.map((pmt, idx) => (
                      <tr key={getPaymentRowId(pmt) || `payment-row-${idx}`}>
                        <td>{formatDate(pmt.Payment_Date ?? pmt.payment_date)}</td>
                    {(() => {
                      const code = String(pmt.Currency_Code ?? pmt.currency_code ?? "").trim().toUpperCase();
                      const sym = pmt.Currency_Symbol ?? pmt.currency_symbol ?? "";
                      const symbolFromCode = code === "INR" ? "₹" : code === "USD" ? "$" : "";
                      const amountSymbol = sym || symbolFromCode || "₹";

                      return (
                        <>
                          <td>
                            {amountSymbol}
                            {Number(pmt.Amount ?? pmt.amount ?? 0).toLocaleString("en-IN")}
                          </td>
                          <td>{amountSymbol === "₹" ? "₹" : amountSymbol === "$" ? "$" : (pmt.Currency_Code ?? pmt.Currency_Name ?? "—")}</td>
                        </>
                      );
                    })()}
                        <td>{pmt.Payment_Mode_Name ?? pmt.payment_mode_name ?? "—"}</td>
                        <td>{pmt.Payment_Type ?? pmt.payment_type ?? "—"}</td>
                    {(() => {
                      const code = String(pmt.Currency_Code ?? pmt.currency_code ?? "").trim().toUpperCase();
                      const sym = pmt.Currency_Symbol ?? pmt.currency_symbol ?? "";
                      const symbolFromCode = code === "INR" ? "₹" : code === "USD" ? "$" : "";
                      const amountSymbol = sym || symbolFromCode || "₹";

                      return (
                        <td>
                          {amountSymbol}
                          {Number(pmt.Total_Amount ?? pmt.total_amount ?? 0).toLocaleString("en-IN")}
                        </td>
                      );
                    })()}
                        <td
                          style={{ cursor: "pointer" }}
                          title="View payment details"
                          onClick={() => {
                            const pid = pmt.Payment_Id ?? pmt.payment_id ?? pmt.id;
                            if (pid != null) navigate(`/project-payments/${pid}/view`);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              const pid = pmt.Payment_Id ?? pmt.payment_id ?? pmt.id;
                              if (pid != null) navigate(`/project-payments/${pid}/view`);
                            }
                          }}
                          role="button"
                          tabIndex={0}
                        >
                          {pmt.Transaction_Ref ?? pmt.transaction_ref ?? "—"}
                        </td>
                        <td>{pmt.Payment_Status ?? pmt.payment_status ?? "—"}</td>
                        <td>{pmt.Remarks ?? pmt.remarks ?? "—"}</td>
                        {canEditProject && (
                          <td>
                            <div className="viewlead-followup-actions" style={{ justifyContent: "center" }}>
                              <button
                                type="button"
                                className="btn-edit-followup"
                                onClick={() => {
                                  const pid = pmt.Payment_Id ?? pmt.payment_id ?? pmt.id;
                                  if (pid != null) navigate(`/project-payments/${pid}/edit`);
                                }}
                                title="Edit payment"
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                className="btn-delete-followup"
                                onClick={async () => {
                                  const pid = pmt.Payment_Id ?? pmt.payment_id ?? pmt.id;
                                  if (pid == null) return;
                                  if (!window.confirm("Are you sure you want to delete this payment?")) return;
                                  try {
                                    await axiosInstance.delete(`/delete-project-payment/${pid}`);
                                    alert("Payment deleted successfully");
                                    fetchPayments();
                                  } catch (err) {
                                    console.error("Error deleting project payment:", err);
                                    alert(
                                      err.response?.data?.message ||
                                        err.response?.data?.error ||
                                        err.message ||
                                        "Failed to delete payment."
                                    );
                                  }
                                }}
                                disabled={!canEditProject}
                                title="Delete payment"
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : (
          <div className="muted-text" style={{ marginTop: 16 }}>No payments recorded yet.</div>
        )}
      </div>

      <div className="viewlead-card project-subscriptions-card">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h3 className="viewlead-section-title" style={{ margin: 0 }}>Project Subscriptions</h3>
        </div>

        {loadingSubscriptions ? (
          <div className="project-subprojects-loading" style={{ marginTop: 16 }}>Loading subscriptions...</div>
        ) : subscriptionsError ? (
          <div className="project-subprojects-loading" style={{ marginTop: 16, color: "#b42318" }}>{subscriptionsError}</div>
        ) : subscriptions.length > 0 ? (
          <div className="project-payments-table">
            <div className="table-wrapper">
              <div className="table-container">
                <table className="data-table projects-table-frozen project-subscriptions-table">
                  <thead>
                    <tr>
                      <th className="projects-th-sortable" {...getSubscriptionColProps(0)}>
                        <div className="projects-th-sort-row">
                          <SupportSortableHeader
                            columnId="plan"
                            label="Plan"
                            sortColumn={subscriptionSortColumn}
                            sortDirection={subscriptionSortDirection}
                            onSort={handleSubscriptionSort}
                          />
                        </div>
                        <SubscriptionResizeHandle index={0} />
                      </th>
                      <th className="projects-th-sortable" {...getSubscriptionColProps(1)}>
                        <div className="projects-th-sort-row projects-th-sort-row--center">
                          <SupportSortableHeader
                            columnId="startDate"
                            label="Start Date"
                            sortColumn={subscriptionSortColumn}
                            sortDirection={subscriptionSortDirection}
                            onSort={handleSubscriptionSort}
                          />
                        </div>
                        <SubscriptionResizeHandle index={1} />
                      </th>
                      <th className="projects-th-sortable" {...getSubscriptionColProps(2)}>
                        <div className="projects-th-sort-row projects-th-sort-row--center">
                          <SupportSortableHeader
                            columnId="endDate"
                            label="End Date"
                            sortColumn={subscriptionSortColumn}
                            sortDirection={subscriptionSortDirection}
                            onSort={handleSubscriptionSort}
                          />
                        </div>
                        <SubscriptionResizeHandle index={2} />
                      </th>
                      <th className="projects-th-sortable" {...getSubscriptionColProps(3)}>
                        <div className="projects-th-sort-row projects-th-sort-row--center">
                          <SupportSortableHeader
                            columnId="duration"
                            label="Duration (days)"
                            sortColumn={subscriptionSortColumn}
                            sortDirection={subscriptionSortDirection}
                            onSort={handleSubscriptionSort}
                          />
                        </div>
                        <SubscriptionResizeHandle index={3} />
                      </th>
                      <th className="projects-th-sortable" {...getSubscriptionColProps(4)}>
                        <div className="projects-th-sort-row projects-th-sort-row--center">
                          <SupportSortableHeader
                            columnId="status"
                            label="Status"
                            sortColumn={subscriptionSortColumn}
                            sortDirection={subscriptionSortDirection}
                            onSort={handleSubscriptionSort}
                          />
                        </div>
                        <SubscriptionResizeHandle index={4} />
                      </th>
                      <th className="projects-th-sortable" {...getSubscriptionColProps(5)}>
                        <div className="projects-th-sort-row projects-th-sort-row--center">
                          <SupportSortableHeader
                            columnId="amount"
                            label="Amount"
                            sortColumn={subscriptionSortColumn}
                            sortDirection={subscriptionSortDirection}
                            onSort={handleSubscriptionSort}
                          />
                        </div>
                        <SubscriptionResizeHandle index={5} />
                      </th>
                      <th className="projects-th-sortable" {...getSubscriptionColProps(6)}>
                        <div className="projects-th-sort-row projects-th-sort-row--center">
                          <SupportSortableHeader
                            columnId="createdOn"
                            label="Created On"
                            sortColumn={subscriptionSortColumn}
                            sortDirection={subscriptionSortDirection}
                            onSort={handleSubscriptionSort}
                          />
                        </div>
                        <SubscriptionResizeHandle index={6} />
                      </th>
                      <th className="projects-th-sortable" {...getSubscriptionColProps(7)}>
                        <div className="projects-th-sort-row projects-th-sort-row--center">
                          <SupportSortableHeader
                            columnId="createdBy"
                            label="Created By"
                            sortColumn={subscriptionSortColumn}
                            sortDirection={subscriptionSortDirection}
                            onSort={handleSubscriptionSort}
                          />
                        </div>
                        {canEditProject ? <SubscriptionResizeHandle index={7} /> : null}
                      </th>
                      {canEditProject && (
                        <th {...getSubscriptionColProps(8)}>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>Actions</div>
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedSubscriptions.map((s, idx) => (
                      <tr key={getSubscriptionRowId(s) || `subscription-row-${idx}`}>
                        <td
                          style={{ cursor: "pointer" }}
                          title="View subscription details"
                          onClick={() => {
                            const sid = s.Subscription_Id ?? s.subscription_id ?? s.id;
                            if (sid != null) navigate(`/project-subscriptions/${sid}/view`);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              const sid = s.Subscription_Id ?? s.subscription_id ?? s.id;
                              if (sid != null) navigate(`/project-subscriptions/${sid}/view`);
                            }
                          }}
                          role="button"
                          tabIndex={0}
                        >
                          {s.Plan_Name ?? "—"}
                        </td>
                        <td>{formatDate(s.Start_Date)}</td>
                        <td>{formatDate(s.End_Date)}</td>
                        <td>{s.Duration_Days ?? "—"}</td>
                        <td>{s.Status ?? "—"}</td>
                        <td>{Number(s.Amount ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        <td>{formatDate(s.Created_On)}</td>
                        <td>{s.Created_By_Name ?? "—"}</td>
                        {canEditProject && (
                          <td>
                            <div className="viewlead-followup-actions" style={{ justifyContent: "center" }}>
                              <button
                                type="button"
                                className="btn-edit-followup"
                                onClick={() => {
                                  const sid = s.Subscription_Id ?? s.subscription_id ?? s.id;
                                  if (sid != null) navigate(`/projects/${projectId}/edit?subscriptionId=${encodeURIComponent(String(sid))}`);
                                }}
                                title="Edit subscription"
                              >
                                <FaEdit />
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : (
          <div className="muted-text" style={{ marginTop: 16 }}>No subscriptions found for this project.</div>
        )}
      </div>

      <div className="viewlead-card project-subprojects-view-card">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h3 className="viewlead-section-title" style={{ margin: 0 }}>Subprojects</h3>
          {canAddSubproject && (
            <button
              type="button"
              className="btn-subproject-primary"
              onClick={() => navigate(`/projects/${projectId}/subprojects/add`)}
            >
              Add Subproject
            </button>
          )}
        </div>
        {!loadingSubprojects && (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 10,
              marginBottom: 12,
              alignItems: "center"
            }}
          >
            <input
              type="text"
              placeholder="Search by subproject name..."
              value={subprojectNameFilter}
              onChange={(e) => {
                setSubprojectNameFilter(e.target.value);
                setSubprojectPage(1);
              }}
              style={{
                height: 32,
                borderRadius: 5,
                border: "1px solid #D6E0EA",
                padding: "0 10px",
                fontSize: 13,
                minWidth: 180
              }}
            />
            <select
              value={subprojectDomainFilter}
              onChange={(e) => {
                setSubprojectDomainFilter(e.target.value);
                setSubprojectPage(1);
              }}
              disabled={loadingDomains}
              style={{
                height: 32,
                borderRadius: 5,
                border: "1px solid #D6E0EA",
                padding: "0 10px",
                fontSize: 13,
                minWidth: 150
              }}
            >
              <option value="">{loadingDomains ? "Loading domains..." : "All Domains"}</option>
              {domains.map((d) => (
                <option key={d.id} value={d.name}>{d.name}</option>
              ))}
            </select>
            <select
              value={subprojectCategoryFilter}
              onChange={(e) => {
                setSubprojectCategoryFilter(e.target.value);
                setSubprojectPage(1);
              }}
              style={{
                height: 32,
                borderRadius: 5,
                border: "1px solid #D6E0EA",
                padding: "0 10px",
                fontSize: 13,
                minWidth: 150
              }}
            >
              <option value="">All Categories</option>
              {subprojectCategoryOptions.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            <select
              value={subprojectStatusFilter}
              onChange={(e) => {
                setSubprojectStatusFilter(e.target.value);
                setSubprojectPage(1);
              }}
              style={{
                height: 32,
                borderRadius: 5,
                border: "1px solid #D6E0EA",
                padding: "0 10px",
                fontSize: 13,
                minWidth: 140
              }}
            >
              <option value="">All Status</option>
              {subprojectStatusOptions.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            <button
              type="button"
              className="btn-clear"
              onClick={() => {
                setSubprojectNameFilter("");
                setSubprojectDomainFilter("");
                setSubprojectCategoryFilter("");
                setSubprojectStatusFilter("");
                setSubprojectPage(1);
              }}
              style={{ marginLeft: 8 }}
            >
              Clear
            </button>
          </div>
        )}
        {loadingSubprojects ? (
          <div className="project-subprojects-loading">Loading subprojects...</div>
        ) : (
          <>
            <div
              className="projects-container"
              style={{ padding: 0, backgroundColor: "transparent", marginTop: 0, minHeight: "auto" }}
            >
              <div className="table-wrapper" style={{ marginBottom: 16 }}>
                <div className="table-container">
                  <table className="data-table projects-table-frozen">
                    <thead>
                      <tr>
                        <th
                          className="col-sticky col-0 projects-th-sortable"
                          style={{
                            minWidth: getSubprojectColumnMinWidth(0) || "160px",
                            width: getSubprojectColumnWidth(0)
                          }}
                        >
                          <div className="projects-th-sort-row">
                            <SupportSortableHeader
                              columnId="name"
                              label="Subproject Name"
                              sortColumn={subprojectSortColumn}
                              sortDirection={subprojectSortDirection}
                              onSort={handleSubprojectSort}
                            />
                          </div>
                          <span
                            className="column-resizer"
                            onMouseDown={(e) => {
                              e.stopPropagation();
                              handleSubprojectResizeStart(e, 0);
                            }}
                          ></span>
                        </th>
                        <th
                          className="projects-th-sortable"
                          style={{
                            minWidth: getSubprojectColumnMinWidth(1) || "120px",
                            width: getSubprojectColumnWidth(1),
                            position: "relative"
                          }}
                        >
                          <div className="projects-th-sort-row projects-th-sort-row--center">
                            <SupportSortableHeader
                              columnId="shortCode"
                              label="Short Code"
                              sortColumn={subprojectSortColumn}
                              sortDirection={subprojectSortDirection}
                              onSort={handleSubprojectSort}
                            />
                          </div>
                          <span
                            className="column-resizer"
                            onMouseDown={(e) => handleSubprojectResizeStart(e, 1)}
                          ></span>
                        </th>
                        <th
                          className="projects-th-sortable"
                          style={{
                            minWidth: getSubprojectColumnMinWidth(2) || "140px",
                            width: getSubprojectColumnWidth(2),
                            position: "relative"
                          }}
                        >
                          <div className="projects-th-sort-row projects-th-sort-row--center">
                            <SupportSortableHeader
                              columnId="domain"
                              label="Domain"
                              sortColumn={subprojectSortColumn}
                              sortDirection={subprojectSortDirection}
                              onSort={handleSubprojectSort}
                            />
                          </div>
                          <span
                            className="column-resizer"
                            onMouseDown={(e) => handleSubprojectResizeStart(e, 2)}
                          ></span>
                        </th>
                        <th
                          className="projects-th-sortable"
                          style={{
                            minWidth: getSubprojectColumnMinWidth(3) || "140px",
                            width: getSubprojectColumnWidth(3),
                            position: "relative"
                          }}
                        >
                          <div className="projects-th-sort-row projects-th-sort-row--center">
                            <SupportSortableHeader
                              columnId="category"
                              label="Category"
                              sortColumn={subprojectSortColumn}
                              sortDirection={subprojectSortDirection}
                              onSort={handleSubprojectSort}
                            />
                          </div>
                          <span
                            className="column-resizer"
                            onMouseDown={(e) => handleSubprojectResizeStart(e, 3)}
                          ></span>
                        </th>
                        <th
                          className="projects-th-sortable"
                          style={{
                            minWidth: getSubprojectColumnMinWidth(4) || "120px",
                            width: getSubprojectColumnWidth(4),
                            position: "relative"
                          }}
                        >
                          <div className="projects-th-sort-row projects-th-sort-row--center">
                            <SupportSortableHeader
                              columnId="status"
                              label="Status"
                              sortColumn={subprojectSortColumn}
                              sortDirection={subprojectSortDirection}
                              onSort={handleSubprojectSort}
                            />
                          </div>
                          <span
                            className="column-resizer"
                            onMouseDown={(e) => handleSubprojectResizeStart(e, 4)}
                          ></span>
                        </th>
                        <th
                          className="projects-th-sortable"
                          style={{
                            minWidth: getSubprojectColumnMinWidth(5) || "130px",
                            width: getSubprojectColumnWidth(5),
                            position: "relative"
                          }}
                        >
                          <div className="projects-th-sort-row projects-th-sort-row--center">
                            <SupportSortableHeader
                              columnId="startDate"
                              label="Start Date"
                              sortColumn={subprojectSortColumn}
                              sortDirection={subprojectSortDirection}
                              onSort={handleSubprojectSort}
                            />
                          </div>
                          <span
                            className="column-resizer"
                            onMouseDown={(e) => handleSubprojectResizeStart(e, 5)}
                          ></span>
                        </th>
                        <th
                          className="projects-th-sortable"
                          style={{
                            minWidth: getSubprojectColumnMinWidth(6) || "130px",
                            width: getSubprojectColumnWidth(6),
                            position: "relative"
                          }}
                        >
                          <div className="projects-th-sort-row projects-th-sort-row--center">
                            <SupportSortableHeader
                              columnId="endDate"
                              label="End Date"
                              sortColumn={subprojectSortColumn}
                              sortDirection={subprojectSortDirection}
                              onSort={handleSubprojectSort}
                            />
                          </div>
                          <span
                            className="column-resizer"
                            onMouseDown={(e) => handleSubprojectResizeStart(e, 6)}
                          ></span>
                        </th>
                        <th
                          className="projects-th-sortable"
                          style={{
                            minWidth: getSubprojectColumnMinWidth(7) || "120px",
                            width: getSubprojectColumnWidth(7),
                            position: "relative"
                          }}
                        >
                          <div className="projects-th-sort-row projects-th-sort-row--center">
                            <SupportSortableHeader
                              columnId="visibility"
                              label="Visibility"
                              sortColumn={subprojectSortColumn}
                              sortDirection={subprojectSortDirection}
                              onSort={handleSubprojectSort}
                            />
                          </div>
                          <span
                            className="column-resizer"
                            onMouseDown={(e) => handleSubprojectResizeStart(e, 7)}
                          ></span>
                        </th>
                        <th
                          className="col-sticky col-action"
                          style={{
                            minWidth: getSubprojectColumnMinWidth(8) || "120px",
                            width: getSubprojectColumnWidth(8)
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>Actions</div>
                          <span
                            className="column-resizer"
                            onMouseDown={(e) => handleSubprojectResizeStart(e, 8)}
                          ></span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedSubprojects.length === 0 ? (
                        <tr>
                          <td colSpan="9" style={{ textAlign: "center", padding: "24px", color: "#5E6C84" }}>
                            No subprojects yet.
                          </td>
                        </tr>
                      ) : (
                        currentSubprojects.map((sp) => {
                          const id = sp.Sub_Project_Id || sp.sub_project_id;
                          return (
                            <tr key={id}>
                              <td className="col-sticky col-0">
                                <span
                                  className="table-name-link"
                                  onClick={() => navigate(`/projects/${projectId}/subprojects/${id}/view`)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      e.preventDefault();
                                      navigate(`/projects/${projectId}/subprojects/${id}/view`);
                                    }
                                  }}
                                  role="button"
                                  tabIndex={0}
                                  style={{ cursor: "pointer", color: "#2977ed" }}
                                >
                                  {sp.Sub_Project_Name || sp.sub_project_name || "—"}
                                </span>
                              </td>
                              <td>{sp.Short_Code || sp.short_code || "—"}</td>
                              <td>{sp.Domain || sp.domain || "—"}</td>
                              <td>{sp.Category || sp.category || "—"}</td>
                              <td>
                                <span className={`status-badge status-${(sp.Status || sp.status || "").toLowerCase().replace(/\s/g, "-")}`}>
                                  {sp.Status || sp.status || "—"}
                                </span>
                              </td>
                              <td>{formatDate(sp.Start_Date || sp.start_date)}</td>
                              <td>{formatDate(sp.End_Date || sp.end_date)}</td>
                              <td>{sp.Visibility || sp.visibility || "—"}</td>
                              <td className="col-sticky col-action">
                                <div className="project-actions-cell">
                                  {canEditSubproject && (
                                    <button
                                      type="button"
                                      className="project-action-btn project-action-btn-icon project-action-btn-edit"
                                      title="Edit subproject"
                                      onClick={() => navigate(`/projects/${projectId}/subprojects/${id}/edit`)}
                                    >
                                      <FaEdit />
                                    </button>
                                  )}
                                  {canDeleteSubproject && (
                                    <button
                                      type="button"
                                      className="project-action-btn project-action-btn-icon project-action-btn-deactivate"
                                      title="Delete subproject"
                                      onClick={() => handleDeleteSubproject(id)}
                                    >
                                      <FaTrash />
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              {!loadingSubprojects && sortedSubprojects.length > 0 && (
                <TablePagination
                  currentPage={subprojectPage}
                  totalItems={sortedSubprojects.length}
                  rowsPerPage={subprojectRowsPerPage}
                  rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
                  onPageChange={setSubprojectPage}
                  onRowsPerPageChange={handleSubprojectRowsPerPageChange}
                  itemLabel="subprojects"
                  rowsPerPageSelectId="view-project-subprojects-rows-per-page"
                />
              )}
            </div>
          </>
        )}
      </div>

      {/* User Stories (show for the project regardless of whether subprojects exist) */}
      {project && (
        <div id="user-stories" className="viewlead-card project-subprojects-view-card">
          <UserStoriesTable
            cardTitle="User Stories"
            storiesScope="project"
            projectId={projectId}
            projectName={p.Project_Name || p.project_name}
          />
        </div>
      )}
    </div>
  );
}

export default ViewProject;

